import { Checkbox } from '@mui/material';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { IoCloseSharp } from 'react-icons/io5';
import { sendUser } from '../../services/properties';
import { isColorLight, trackEvent } from '../../utils/FunctionsGlobals';
import { useSiteRegiao } from '../context/Info';
import { AppPhoneInput } from '../input_phone/AppPhoneInput';
import styles from './LeadContactModal.module.css';

type LeadContactModalProps = {
    open: boolean;
    onClose: () => void;
    site: any;
    origin?: any;
    codsite?: any;
    title?: string;
    message?: string;
    source?: string;
    entityId?: number;
};

const getQueryStringValue = (value: any) => {
    if (Array.isArray(value)) return String(value[0] || '');
    return String(value || '');
};

const isPhoneNumberValid = (phoneNumber: string) => {
    const cleanedNumber = phoneNumber.replace(/\D/g, '');
    if (!cleanedNumber) return false;
    const allEqual = new Set(cleanedNumber).size === 1;
    return !allEqual;
};

const LeadContactModal: React.FC<LeadContactModalProps> = ({
    open,
    onClose,
    site: siteProp,
    origin,
    codsite,
    title,
    message,
    source = 'lead-modal',
    entityId
}) => {
    // MULTI-REGIÃO: contatos (tel1/tel2/email1) trocados pelos da região ativa quando houver
    const site = useSiteRegiao(siteProp);
    const router = useRouter();
    const { utm_campaign, utm_source, utm_medium, utm_id } = router.query;

    const [name, setName] = useState('');
    const [telephone, setTelephone] = useState('');
    const [email, setEmail] = useState('');
    const [acceptPolitic, setAcceptPolitic] = useState(false);
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const corFundo = site?.cor5 || '#ffffff';
    const corTexto = isColorLight(corFundo) ? (site?.cor11 || '#1f2937') : (site?.cor13 || '#f8fafc');
    const corBotao = site?.cor2 || '#0f4c75';
    const corBotaoTexto = isColorLight(corBotao) ? (site?.cor11 || '#111827') : (site?.cor13 || '#ffffff');

    const submitDisabled = useMemo(() => {
        if (loading) return true;
        if (!name.trim() || !telephone.trim() || !email.trim() || !acceptPolitic) return true;
        if (!/\S+@\S+\.\S+/.test(email)) return true;
        const cleanedPhone = telephone.replace(/\D/g, '');
        if (cleanedPhone.length < 10 || cleanedPhone.length > 16) return true;
        if (!isPhoneNumberValid(cleanedPhone)) return true;
        return false;
    }, [acceptPolitic, email, loading, name, telephone]);

    const resetState = () => {
        setAcceptPolitic(false);
        setLoading(false);
        setSubmitted(false);
        setErrorMessage('');
    };

    useEffect(() => {
        if (!open || typeof window === 'undefined') return;

        resetState();

        try {
            const savedUser = JSON.parse(localStorage.getItem('user') || '{}');
            setName(String(savedUser?.name || savedUser?.nome || ''));
            setEmail(String(savedUser?.email || ''));
            setTelephone(String(savedUser?.telephone || savedUser?.telefone || ''));
        } catch (error) {
            setName('');
            setEmail('');
            setTelephone('');
        }
    }, [open]);

    useEffect(() => {
        if (!open || typeof window === 'undefined') return;

        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        const onEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        window.addEventListener('keydown', onEscape);

        return () => {
            document.body.style.overflow = originalOverflow;
            window.removeEventListener('keydown', onEscape);
        };
    }, [open, onClose]);

    const actionSendUser = async () => {
        if (submitDisabled) return;

        setLoading(true);
        setErrorMessage('');

        const cleanPhone = telephone.replace(/\D/g, '');
        const validPhone = isPhoneNumberValid(cleanPhone);

        if (!validPhone) {
            setLoading(false);
            setErrorMessage('Preencha um numero de telefone valido.');
            return;
        }

        try {
            const response = await sendUser(
                site?.internacional ? true : false,
                JSON.stringify(getQueryStringValue(utm_campaign)),
                JSON.stringify(getQueryStringValue(utm_source)),
                JSON.stringify(getQueryStringValue(utm_medium)),
                JSON.stringify(getQueryStringValue(utm_id)),
                JSON.stringify(message || `Tenho interesse no empreendimento ${title || ''}`),
                JSON.stringify(name),
                JSON.stringify(email),
                JSON.stringify(telephone),
                Number(entityId) > 0 ? Number(entityId) : 0,
                0,
                origin || '',
                codsite || 0,
                '',
                0
            );

            if (!response?.atendimento?.id_cliente) {
                setErrorMessage('Nao foi possivel enviar agora. Tente novamente.');
                setLoading(false);
                return;
            }

            if (typeof window !== 'undefined') {
                localStorage.setItem('user', JSON.stringify({
                    name,
                    email,
                    telephone,
                    id_client: response?.atendimento?.id_cliente
                }));
            }

            trackEvent('generate_lead', {
                source,
                client_id: response?.atendimento?.id_cliente,
                lead_context: 'condominios'
            });

            setSubmitted(true);
        } catch (error) {
            setErrorMessage('Nao foi possivel enviar agora. Tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    if (!open) return null;

    return (
        <div
            className={styles.overlay}
            onClick={onClose}
            role="presentation"
        >
            <div
                className={styles.modal}
                role="dialog"
                aria-modal="true"
                onClick={(event) => event.stopPropagation()}
                style={{
                    ['--lead-bg' as any]: corFundo,
                    ['--lead-ink' as any]: corTexto,
                    ['--lead-accent' as any]: corBotao,
                    ['--lead-accent-ink' as any]: corBotaoTexto
                }}
            >
                <button
                    type="button"
                    className={styles.closeButton}
                    onClick={onClose}
                    aria-label="Fechar modal"
                >
                    <IoCloseSharp size={20} />
                </button>

                {!submitted ? (
                    <>
                        <div className={styles.header}>
                            <h3>{title || 'Anotar interesse'}</h3>
                            <p>
                                Deixe seus dados para receber atendimento.
                            </p>
                        </div>

                        <div className={styles.form}>
                            <input
                                className={styles.input}
                                placeholder="Nome"
                                value={name}
                                onChange={(event) => setName(event.target.value)}
                            />

                            <AppPhoneInput
                                id="lead-contact-phone"
                                value={telephone}
                                onChange={setTelephone}
                                placeholder="Telefone"
                                defaultCountry="BR"
                                width_max={100}
                                inputHeight={48}
                                limitMaxLength
                                inputClassName={styles.input}
                                semPais={!site?.internacional}
                                includeCountryCode={site?.internacional ? true : false}
                            />

                            <input
                                className={styles.input}
                                placeholder="Email"
                                value={email}
                                onChange={(event) => setEmail(event.target.value)}
                            />

                            <label className={styles.termsRow}>
                                <Checkbox
                                    checked={acceptPolitic}
                                    onChange={(_, checked) => setAcceptPolitic(checked)}
                                    style={{ color: 'rgb(126, 126, 126)', padding: 0, marginRight: 8 }}
                                />
                                <span>
                                    Ao enviar concordo com os
                                    {' '}
                                    <Link prefetch={false} href="/termos" className={styles.termsLink}>termos de uso</Link>
                                    {' '}e{' '}
                                    <Link prefetch={false} href="/politica" className={styles.termsLink}>politica de privacidade</Link>.
                                </span>
                            </label>

                            {errorMessage ? (
                                <p className={styles.errorText}>{errorMessage}</p>
                            ) : null}

                            <button
                                type="button"
                                className={submitDisabled ? styles.submitDisabled : styles.submit}
                                onClick={actionSendUser}
                                disabled={submitDisabled}
                            >
                                {loading ? 'Enviando...' : 'Enviar'}
                            </button>
                        </div>
                    </>
                ) : (
                    <div className={styles.successBox}>
                        <h3>Mensagem enviada</h3>
                        <p>Recebemos seus dados e retornaremos em breve.</p>
                        <button
                            type="button"
                            className={styles.submit}
                            onClick={onClose}
                        >
                            Fechar
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LeadContactModal;
